'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ActivityItem {
  id: string;
  tipo: string | null;
  messaggio: string;
  dettaglio: string | null;
  ip_address: string | null;
  created_at: string;
}

const tipoConfig: Record<string, { icon: string; iconColor: string; bg: string }> = {
  conflitto_rilevato: { icon: 'ExclamationTriangleIcon', iconColor: 'text-red-400', bg: 'bg-red-500/10' },
  ip_assegnato: { icon: 'PlusCircleIcon', iconColor: 'text-sky-400', bg: 'bg-sky-500/10' },
  ip_rilasciato: { icon: 'MinusCircleIcon', iconColor: 'text-zinc-400', bg: 'bg-zinc-500/10' },
  dispositivo_offline: { icon: 'XCircleIcon', iconColor: 'text-red-400', bg: 'bg-red-500/10' },
  dispositivo_online: { icon: 'CheckCircleIcon', iconColor: 'text-green-400', bg: 'bg-green-500/10' },
  dispositivo_aggiunto: { icon: 'PlusCircleIcon', iconColor: 'text-sky-400', bg: 'bg-sky-500/10' },
  dispositivo_modificato: { icon: 'PencilSquareIcon', iconColor: 'text-amber-400', bg: 'bg-amber-500/10' },
  dispositivo_eliminato: { icon: 'TrashIcon', iconColor: 'text-red-400', bg: 'bg-red-500/10' },
  subnet_creata: { icon: 'CircleStackIcon', iconColor: 'text-purple-400', bg: 'bg-purple-500/10' },
  utente_login: { icon: 'UserCircleIcon', iconColor: 'text-sky-400', bg: 'bg-sky-500/10' },
};

const defaultConfig = { icon: 'ClipboardDocumentListIcon', iconColor: 'text-muted-color', bg: 'bg-surface-elevated' };

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Adesso';
  if (mins < 60) return `${mins} min fa`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ora${hrs > 1 ? 'e' : ''} fa`;
  const days = Math.floor(hrs / 24);
  return `${days} giorno${days > 1 ? 'i' : ''} fa`;
}

export default function ActivityFeed() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const { data } = await supabase
          .from('activity_log')
          .select('id, tipo, messaggio, dettaglio, ip_address, created_at')
          .order('created_at', { ascending: false })
          .limit(8);
        setActivities((data || []) as ActivityItem[]);
      } catch {
        // silent — RLS may restrict access
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();

    const channel = supabase
      .channel('activity_feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_log' }, () => fetchActivities())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return (
    <div className="bg-surface border border-border rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-bold text-white">Attività Recente</h2>
        <Link href="/storico-eventi" className="text-sm text-sky-400 hover:text-sky-300 transition-colors font-semibold">Vedi tutto</Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-lg bg-surface-elevated flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-surface-elevated rounded w-3/4" />
                <div className="h-2.5 bg-surface-elevated rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : activities.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <Icon name="ClipboardDocumentListIcon" size={32} className="text-muted-color" />
          <p className="text-sm text-muted-color">Nessuna attività recente</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {activities.map((a) => {
            const cfg = tipoConfig[a.tipo || ''] || defaultConfig;
            return (
              <li key={a.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.bg}`}>
                  <Icon name={cfg.icon as any} size={16} className={cfg.iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{a.messaggio}</p>
                  <p className="text-xs text-muted-color truncate">{a.dettaglio || a.ip_address || ''}</p>
                </div>
                <span className="text-xs text-muted-color flex-shrink-0 tabular-nums">{timeAgo(a.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}